import { useQuery } from '@tanstack/react-query';
import { firstValueFrom } from 'rxjs';
import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import { FloorplanPlacement } from 'floorplan/pages/native-floorplan/types';
import { normalizeFloorplanPlacements } from 'floorplan/pages/native-floorplan/utils/normalize-placements';

// Availability is computed server-side at the current time, so keep the cache short — fresh
// enough for the map without refetching on every render.
const STALE_TIME = 60 * 1000;

// Stable reference for the disabled / not-yet-loaded case, so consumers can use `placements`
// in a dependency array without re-running on every render.
const EMPTY: FloorplanPlacement[] = [];

export const FLOORPLAN_MAP_QUERY_KEY = 'floorplan-map';

interface UseFloorplanMapQueryParams {
  floorplanUuid?: string;
  /** Selected day as YYYY-MM-DD. Re-keys the query so changing the date refetches. */
  date?: string;
  enabled?: boolean;
}

export interface UseFloorplanMapQueryResult {
  placements: FloorplanPlacement[];
  isLoading: boolean;
  isError: boolean;
  /** Loaded successfully, but this floorplan has nothing bookable placed on it. */
  isEmpty: boolean;
  refetch: VoidFunction;
}

/**
 * Placements + availability for one floorplan, normalized to `{ resource, geometry, sections,
 * availability }` for the native map.
 *
 * Note the source: the FE tech plan (§21.4) specifies `getDaily?include=floorplan`, which the BE
 * has not implemented — `DailyCalendarViewQueryDto` carries no `include`. This uses the endpoint
 * that does exist and is already consumed by resource-booking-admin,
 * `GET .../floorplans/:floorplanUuid/resources/availability`, which is gated on READ_BOOKINGS and
 * therefore reachable by an employee.
 */
export const useFloorplanMapQuery = ({
  floorplanUuid,
  date,
  enabled = true,
}: UseFloorplanMapQueryParams): UseFloorplanMapQueryResult => {
  const { getClient, buildingUuid } = useResourceBookingServiceClient();

  const isEnabled = enabled && !!buildingUuid && !!floorplanUuid;

  const query = useQuery({
    queryKey: [FLOORPLAN_MAP_QUERY_KEY, buildingUuid, floorplanUuid, date],
    queryFn: async (): Promise<FloorplanPlacement[]> => {
      const client = getClient();
      const resources = await firstValueFrom(
        client.getFloorplanResourcesAvailability(buildingUuid as string, floorplanUuid as string, date),
      );

      return normalizeFloorplanPlacements(resources);
    },
    enabled: isEnabled,
    staleTime: STALE_TIME,
  });

  const placements = query.data ?? EMPTY;

  return {
    placements,
    // `isLoading` stays false while the query is disabled — a missing floorplan uuid is not a
    // pending fetch, and rendering a spinner for it would never resolve.
    isLoading: isEnabled && query.isLoading,
    isError: query.isError,
    isEmpty: query.isSuccess && placements.length === 0,
    refetch: query.refetch,
  };
};
